/**
 * SafeHtmlPreview - the ONLY sanctioned way to show untrusted HTML in
 * the portal (OCP-544).
 *
 * Mail-template bodies are authored by a portal member and read back by
 * other members of the same customer. "Same customer" is not a trust
 * boundary - roles differ inside it - so the preview must not be able to
 * run anything in the portal origin. The previous implementation piped
 * the stored body through `dangerouslySetInnerHTML`, which made an
 * `<img onerror=...>` in a template execute in the reader's authenticated
 * session (billing, API keys, AI keys).
 *
 * The fix is structural rather than a filter list: the markup is rendered
 * inside an iframe with an EMPTY `sandbox` attribute. That is the maximum
 * restriction the platform offers - opaque origin, no scripts, no forms,
 * no top-level navigation, no popups, no same-origin access. Nothing has
 * to be enumerated, so nothing has to be maintained as email HTML gets
 * weirder. An allowlist sanitizer would have to keep up with every new
 * mutation-XSS trick; this does not.
 *
 * Layers behind it, so no single mistake is fatal:
 *   1. `sandbox=""` on the frame (this file)
 *   2. a per-document CSP inside the frame (this file)
 *   3. the parent page CSP from nginx.conf, which srcdoc documents
 *      inherit
 *   4. the store-time active-content gate in the API (OCP-544)
 *
 * The eslint `no-restricted-syntax` rule in .eslintrc.cjs forbids
 * `dangerouslySetInnerHTML` repo-wide, so a sibling call site cannot
 * quietly reintroduce the raw sink.
 */

/**
 * Empty string = every sandbox restriction on. Exported so tests can
 * assert the exact value rather than a "contains" match: adding
 * `allow-scripts` here would re-open the hole, and a substring assertion
 * would not notice.
 */
export const UNTRUSTED_PREVIEW_SANDBOX = '';

/**
 * Document-level CSP injected into the preview frame. `img-src` allows
 * remote https images because real templates reference hosted assets and
 * a preview that renders no images is not a preview. No script source of
 * any kind is permitted, and there is no `frame-src`, so the payload
 * cannot nest another document.
 */
export const UNTRUSTED_PREVIEW_CSP = [
  "default-src 'none'",
  "img-src https: data: cid:",
  "style-src 'unsafe-inline' https:",
  "font-src https: data:",
  "media-src https: data:",
  "form-action 'none'",
  "base-uri 'none'",
].join('; ');

/** Minimal frame chrome so the preview reads like a mail client body. */
const FRAME_STYLES = [
  'html,body{margin:0;padding:12px;background:#ffffff;color:#111827;}',
  'body{font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;font-size:14px;line-height:1.5;}',
  'img{max-width:100%;height:auto;}',
].join('');

/**
 * Wrap untrusted markup in a self-contained document. The result is only
 * ever handed to `srcdoc`, where React escapes it as an attribute value -
 * the payload cannot break out of the attribute and reach the parent DOM.
 */
export function buildPreviewDocument(html: string): string {
  return [
    '<!doctype html><html><head><meta charset="utf-8">',
    `<meta http-equiv="Content-Security-Policy" content="${UNTRUSTED_PREVIEW_CSP}">`,
    '<meta name="referrer" content="no-referrer">',
    `<style>${FRAME_STYLES}</style></head><body>`,
    html,
    '</body></html>',
  ].join('');
}

export interface SafeHtmlPreviewProps {
  /** Untrusted markup. Substitute placeholders BEFORE passing it in. */
  html: string;
  /** Accessible name for the frame. */
  title: string;
  /** Extra classes for the wrapping frame element. */
  className?: string;
  /** Frame height (Tailwind class). Defaults to a comfortable pane. */
  heightClassName?: string;
  /** `data-testid` for tests + support screenshots. */
  testId?: string;
}

export function SafeHtmlPreview({
  html,
  title,
  className,
  heightClassName = 'h-64',
  testId,
}: SafeHtmlPreviewProps) {
  return (
    <iframe
      title={title}
      data-testid={testId}
      sandbox={UNTRUSTED_PREVIEW_SANDBOX}
      referrerPolicy="no-referrer"
      loading="lazy"
      srcDoc={buildPreviewDocument(html)}
      className={[
        'w-full rounded border border-gray-200 dark:border-gray-800 bg-white',
        heightClassName,
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
    />
  );
}
